import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Wind, Droplets, Heart, Activity, Smile, Moon,
  Frown, Meh, CloudRain, Zap, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WellnessCheckIn = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  
  // Form state
  const [mood, setMood] = useState('');
  const [stressLevel, setStressLevel] = useState(3);
  const [sleepHours, setSleepHours] = useState('');
  const [sleepQuality, setSleepQuality] = useState(3);
  const [notes, setNotes] = useState('');

  const moods = [
    { value: 'happy', label: 'Happy', emoji: '😊', color: 'bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100' },
    { value: 'calm', label: 'Calm', emoji: '😌', color: 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100' },
    { value: 'tired', label: 'Tired', emoji: '😴', color: 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100' },
    { value: 'anxious', label: 'Anxious', emoji: '😰', color: 'bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100' },
    { value: 'overwhelmed', label: 'Overwhelmed', emoji: '😓', color: 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100' }
  ];

  useEffect(() => {
    if (user) {
      fetchWellnessInsights();
    }
  }, [user]);

  const fetchWellnessInsights = async () => {
    try {
      const { data } = await axios.get(`${API}/wellness/insights`, {
        withCredentials: true
      });
      setInsights(data);
    } catch (error) {
      console.error('Error fetching wellness insights:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mood) {
      toast.error('Please select how you\'re feeling');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/wellness/check-in`, {
        mood,
        stress_level: stressLevel,
        sleep_hours: sleepHours ? parseFloat(sleepHours) : null,
        sleep_quality: sleepQuality,
        notes: notes || null
      }, { withCredentials: true });
      
      toast.success('Wellness check-in logged! 🌿');
      
      // Reset form
      setMood('');
      setStressLevel(3);
      setSleepHours('');
      setSleepQuality(3);
      setNotes('');
      
      // Refresh insights
      await fetchWellnessInsights();
      
    } catch (error) {
      console.error('Error submitting wellness check-in:', error);
      toast.error('Failed to log check-in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 md:top-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Wellness Check-In</h1>
              <p className="text-sm text-gray-600">Track your mental and physical wellness for early awareness</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Wellness Summary */}
        {insights?.wellness_summary && (
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Heart className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Your Recent Wellness</p>
                  <p className="text-sm text-gray-700">{insights.wellness_summary}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Check-In Form */}
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Mood Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">How are you feeling right now?</CardTitle>
                  <CardDescription>Select the mood that best describes how you feel</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {moods.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => setMood(m.value)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          mood === m.value
                            ? `${m.color} border-current shadow-md scale-105`
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-3xl mb-1">{m.emoji}</div>
                        <div className="text-xs font-medium">{m.label}</div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Stress Level */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">What's your stress level?</CardTitle>
                  <CardDescription>1 = Very calm, 5 = Very stressed</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={stressLevel}
                      onChange={(e) => setStressLevel(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 font-medium">Very Calm</span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{['😌', '🙂', '😐', '😟', '😰'][stressLevel - 1]}</span>
                        <Badge variant="outline" className="text-lg font-bold">
                          {stressLevel}
                        </Badge>
                      </div>
                      <span className="text-red-600 font-medium">Very Stressed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sleep Input */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">How did you sleep last night?</CardTitle>
                  <CardDescription>Optional - helps us give you better wellness tips</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="sleepHours">Hours of Sleep</Label>
                      <Input
                        id="sleepHours"
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        placeholder="7.5"
                        value={sleepHours}
                        onChange={(e) => setSleepHours(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sleepQuality">Sleep Quality (1-5)</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          id="sleepQuality"
                          type="range"
                          min="1"
                          max="5"
                          value={sleepQuality}
                          onChange={(e) => setSleepQuality(parseInt(e.target.value))}
                          className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <Badge variant="outline" className="w-12 justify-center">
                          {sleepQuality}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Anything on your mind?</CardTitle>
                  <CardDescription>Optional - a safe space for your thoughts</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="You can write about what's affecting your mood, what helped you today, or anything you'd like to remember..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || !mood}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6 text-base"
              >
                {loading ? 'Saving...' : 'Complete Wellness Check-In'}
              </Button>
            </form>
          </div>

          {/* Wellness Tips Sidebar */}
          <div className="space-y-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-600" />
                  Wellness Tips for You
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights?.personalized_tips && insights.personalized_tips.length > 0 ? (
                  insights.personalized_tips.map((tip) => (
                    <div key={tip.id} className="p-3 bg-white rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-sm text-gray-900 mb-1">{tip.title}</h4>
                      <p className="text-xs text-gray-700">{tip.message}</p>
                    </div>
                  ))
                ) : (
                  <div className="p-3 bg-white rounded-lg border border-blue-200 text-center">
                    <p className="text-sm text-gray-600">Complete a check-in to get personalized wellness tips</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* General Wellness Reminder */}
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="text-center space-y-2">
                  <div className="text-3xl">🌿</div>
                  <p className="text-sm font-medium text-gray-900">Remember</p>
                  <p className="text-xs text-gray-700">
                    Your well-being matters. Small steps each day make a big difference.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WellnessCheckIn;
